/**
 * Plan metadata helpers (UI-4) — parse and rewrite the directive comments
 * the planner embeds in `/plan.md`. Today the only supported directives are
 * `<!-- deliverable_type: KEY -->` (parsed in DeliverableView) and
 * `<!-- execution_mode: KEY -->` (added here, surfaced as a dropdown the
 * user can override before approving).
 *
 * Kept in a pure module so the parsing rules are unit-testable without
 * standing up the React tree.
 */

// Mirrors the backend registry in
// ``app/backend/approaches/explorerdeep/execution_modes.py``. Keep in sync —
// the planner emits one of these values in the <!-- execution_mode: X -->
// directive, the gate middleware bounds tool dispatches against the same
// set, and the dropdown lets the user pick from the same list.
export const EXECUTION_MODES = ["reuse_scout", "validate_once", "full_worker_execution"] as const
export type ExecutionMode = (typeof EXECUTION_MODES)[number]

const EXECUTION_MODE_COMMENT_RE = /<!--\s*execution_mode\s*:\s*([a-z_]+)\s*-->/i

/** Extract the execution_mode directive from a plan markdown blob. */
export function parseExecutionMode(content: string): ExecutionMode | null {
  const match = EXECUTION_MODE_COMMENT_RE.exec(content)
  if (!match) return null
  const key = match[1].toLowerCase() as ExecutionMode
  return EXECUTION_MODES.includes(key) ? key : null
}

/**
 * Replace or insert the execution_mode directive. When the directive is
 * already present, swap its value in place (keeps surrounding comments in
 * the same spot). When absent, prepend it as the first line so it lands
 * alongside other directive comments.
 */
export function setExecutionMode(content: string, mode: ExecutionMode): string {
  if (EXECUTION_MODE_COMMENT_RE.test(content)) {
    return content.replace(EXECUTION_MODE_COMMENT_RE, `<!-- execution_mode: ${mode} -->`)
  }
  const directive = `<!-- execution_mode: ${mode} -->`
  return content.length === 0 ? directive : `${directive}\n${content}`
}

const SOURCES_HEADER_RE =
  /^#{1,3}\s+(Källor jag planerar att konsultera|Källor|Sources( I['’]ll consult)?)\s*$/i
const NEXT_HEADER_RE = /^#{1,3}\s+/

/**
 * Remove a single source line from the "Planned sources" section of a plan
 * markdown blob. Match is by exact source text (the same string the brief
 * header rendered) — bullets / numbering on the line are stripped before
 * comparison so user-clicked chips line up with their underlying lines.
 */
export function removePlanSource(content: string, sourceText: string): string {
  const lines = content.split("\n")
  const target = sourceText.trim()
  let inSection = false
  const out: string[] = []
  let removed = false
  for (const line of lines) {
    if (SOURCES_HEADER_RE.test(line.trim())) {
      inSection = true
      out.push(line)
      continue
    }
    if (inSection && NEXT_HEADER_RE.test(line)) {
      inSection = false
      out.push(line)
      continue
    }
    if (inSection && !removed) {
      const cleaned = line.replace(/^[-*\d.]+\s*/, "").trim()
      if (cleaned === target) {
        removed = true
        continue
      }
    }
    out.push(line)
  }
  return out.join("\n")
}
